import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import {
  fetchConversations,
  fetchMessages as fetchMessagesApi,
  fetchUnreadByCompany,
  CompanyUnreadEntry,
} from "services/chat.service";
import {
  BroadcastMessageReaction,
  ChatMessage,
  Conversation,
  MessageReaction,
  MiniChatState,
} from "types/Chat";

export type PanelTab = "online" | "chats";

interface ThreadState {
  conversationId: string;
  messages: ChatMessage[];
  hasMore: boolean;
  nextCursor: number | null;
  isLoadingOlder: boolean;
  /** True after the first full history page has been fetched from the API. */
  historyLoaded: boolean;
}

interface ChatState {
  activeTab: PanelTab;
  conversations: Conversation[];
  conversationsLoaded: boolean;
  // Currently open conversation in the chats tab. null = list view.
  openConversationId: string | null;
  openConversationCompanyId: string | null;
  threads: Record<string, ThreadState>;
  // Cross-company unread aggregation: { [companyId]: { count, name } }.
  unreadByCompany: Record<string, CompanyUnreadEntry>;
  activeMiniChats: MiniChatState[];
  /** Conversations opened in mini-chat from other companies (not mixed into the side-panel list). */
  miniChatConversations: Record<string, Conversation>;
  isLauncherExpanded: boolean;
  conversationCompanyMap: Record<string, string>;
}

/** API returns newest-first; UI renders oldest-first. */
function normalizeMessagesPage(messages: ChatMessage[]): ChatMessage[] {
  return [...messages].reverse();
}

function mergeMessagePages(
  existing: ChatMessage[],
  incoming: ChatMessage[]
): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  for (const m of existing) byId.set(m.id, m);
  for (const m of incoming) byId.set(m.id, m);
  return Array.from(byId.values()).sort((a, b) => a.createdAt - b.createdAt);
}

/** Keep exactly one expanded mini-chat; all others minimized. */
function collapseAllMiniExcept(state: ChatState, keepExpandedConversationId: string) {
  const id = String(keepExpandedConversationId);
  for (const c of state.activeMiniChats) {
    c.isMinimized = String(c.conversationId) !== id;
  }
}

function isMiniChatActivelyViewed(state: ChatState, conversationId: string): boolean {
  return state.activeMiniChats.some(
    (c) => String(c.conversationId) === String(conversationId) && !c.isMinimized
  );
}


const TAB_STORAGE_KEY = "socialPanelActiveTab";

function readTab(): PanelTab {
  try {
    const v = localStorage.getItem(TAB_STORAGE_KEY);
    if (v === "chats" || v === "online") return v;
  } catch { /* ignore */ }
  return "online";
}

function persistTab(tab: PanelTab) {
  try {
    localStorage.setItem(TAB_STORAGE_KEY, tab);
  } catch { /* ignore */ }
}

const lastConversationKey = (companyId: string) => `socialPanelLastConv:${companyId}`;

export function readLastConversation(companyId: string): string | null {
  try {
    return localStorage.getItem(lastConversationKey(companyId));
  } catch {
    return null;
  }
}

function persistLastConversation(companyId: string, conversationId: string | null) {
  try {
    if (conversationId) {
      localStorage.setItem(lastConversationKey(companyId), conversationId);
    } else {
      localStorage.removeItem(lastConversationKey(companyId));
    }
  } catch { /* ignore */ }
}

const initialState: ChatState = {
  activeTab: readTab(),
  conversations: [],
  conversationsLoaded: false,
  openConversationId: null,
  openConversationCompanyId: null,
  threads: {},
  unreadByCompany: {},
  activeMiniChats: [],
  miniChatConversations: {},
  isLauncherExpanded: false,
  conversationCompanyMap: {},
};

export const fetchConversationsThunk = createAsyncThunk(
  "chat/fetchConversations",
  async (companyId: string) => fetchConversations(companyId)
);

export const fetchUnreadByCompanyThunk = createAsyncThunk(
  "chat/fetchUnreadByCompany",
  async () => fetchUnreadByCompany()
);

export const fetchMessagesThunk = createAsyncThunk(
  "chat/fetchMessages",
  async (args: {
    companyId: string;
    conversationId: string;
    cursor?: number;
    limit?: number;
  }) => {
    const res = await fetchMessagesApi(args.companyId, args.conversationId, {
      cursor: args.cursor,
      limit: args.limit,
    });
    return {
      ...res,
      conversationId: args.conversationId,
      cursor: args.cursor,
    };
  }
);

/** Load conversation metadata for a cross-company mini-chat without replacing the side-panel list. */
export const fetchMiniChatConversationMetaThunk = createAsyncThunk(
  "chat/fetchMiniChatConversationMeta",
  async (args: { companyId: string; conversationId: string }) => {
    const conversations = await fetchConversations(args.companyId);
    const convo = conversations.find(
      (c) => String(c.id) === String(args.conversationId)
    );
    if (!convo) {
      throw new Error("Conversation not found");
    }
    return { conversationId: args.conversationId, companyId: args.companyId, convo };
  }
);

export const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setActiveTab(state, action: PayloadAction<PanelTab>) {
      state.activeTab = action.payload;
      persistTab(action.payload);
    },
    openConversation(
      state,
      action: PayloadAction<{
        companyId: string;
        conversationId: string | null;
        /** When true, do not touch localStorage (e.g. clearing stale state after company switch). */
        skipPersist?: boolean;
      }>
    ) {
      const { companyId, conversationId, skipPersist } = action.payload;
      state.openConversationId = conversationId;
      state.openConversationCompanyId = companyId;
      if (!skipPersist) {
        persistLastConversation(companyId, conversationId);
      }
    },
    receiveMessage(state, action: PayloadAction<ChatMessage>) {
      const m = action.payload;
      let thread = state.threads[m.conversationId];
      if (!thread) {
        thread = {
          conversationId: m.conversationId,
          messages: [],
          hasMore: true,
          nextCursor: null,
          isLoadingOlder: false,
          historyLoaded: false,
        };
        state.threads[m.conversationId] = thread;
      }
      const idx = thread.messages.findIndex((x) => x.id === m.id);
      if (idx >= 0) {
        thread.messages[idx] = { ...thread.messages[idx], ...m };
      } else {
        thread.messages.push(m);
      }
      thread.messages.sort((a, b) => a.createdAt - b.createdAt);

      // Update preview in the conversations list / mini-chat cache
      const preview = {
        id: m.id,
        preview: m.content || (m.attachment ? m.attachment.name : ""),
        createdAt: m.createdAt,
      };
      const convo = state.conversations.find((c) => c.id === m.conversationId);
      if (convo) convo.lastMessage = preview;
      const miniConvo = state.miniChatConversations[m.conversationId];
      if (miniConvo) miniConvo.lastMessage = preview;
    },
    addOptimisticMessage(state, action: PayloadAction<ChatMessage>) {
      const m = action.payload;
      const thread = state.threads[m.conversationId];
      if (thread) {
        thread.messages.push(m);
      }
    },
    replaceOptimisticMessage(
      state,
      action: PayloadAction<{ tempId: string; message: ChatMessage }>
    ) {
      const { tempId, message } = action.payload;
      const thread = state.threads[message.conversationId];
      if (thread) {
        const tempIdx = thread.messages.findIndex((m) => m.id === tempId);
        const existingIdx = thread.messages.findIndex((m) => m.id === message.id);

        if (tempIdx >= 0) {
          if (existingIdx >= 0) {
            // Socket already delivered the message. Just remove the temp one.
            thread.messages.splice(tempIdx, 1);
          } else {
            // Normal case: replace temp with real
            thread.messages[tempIdx] = message;
          }
        }
      }
      // Update preview
      const convo = state.conversations.find((c) => c.id === message.conversationId);
      if (convo) {
        convo.lastMessage = {
          id: message.id,
          preview: message.content || (message.attachment ? message.attachment.name : ""),
          createdAt: message.createdAt,
        };
      }
    },
    removeOptimisticMessage(
      state,
      action: PayloadAction<{ conversationId: string; tempId: string }>
    ) {
      const { conversationId, tempId } = action.payload;
      const thread = state.threads[conversationId];
      if (thread) {
        thread.messages = thread.messages.filter((m) => m.id !== tempId);
      }
    },
    bumpUnread(state, action: PayloadAction<{ conversationId: string }>) {
      const convo = state.conversations.find((c) => c.id === action.payload.conversationId);
      if (convo) convo.unreadCount++;
      const miniConvo = state.miniChatConversations[action.payload.conversationId];
      if (miniConvo) miniConvo.unreadCount++;
    },
    clearUnread(state, action: PayloadAction<{ conversationId: string; lastReadAt: number }>) {
      const { conversationId, lastReadAt } = action.payload;
      let countToDeduct = 0;
      let companyIdToDeduct: string | null = null;
      
      const convo = state.conversations.find((c) => c.id === conversationId);
      if (convo) {
        countToDeduct = convo.unreadCount || 0;
        convo.unreadCount = 0;
        convo.lastReadAt = lastReadAt;
      }
      const miniConvo = state.miniChatConversations[conversationId];
      if (miniConvo) {
        if (!countToDeduct) countToDeduct = miniConvo.unreadCount || 0;
        miniConvo.unreadCount = 0;
        miniConvo.lastReadAt = lastReadAt;
        const miniChat = state.activeMiniChats.find((c) => String(c.conversationId) === String(conversationId));
        if (miniChat) companyIdToDeduct = miniChat.companyId;
        else if (state.conversationCompanyMap[conversationId]) companyIdToDeduct = state.conversationCompanyMap[conversationId];
      }

      if (countToDeduct > 0 && companyIdToDeduct && state.unreadByCompany[companyIdToDeduct]) {
        state.unreadByCompany[companyIdToDeduct].count = Math.max(0, state.unreadByCompany[companyIdToDeduct].count - countToDeduct);
      }
    },
    applyMessageEdited(
      state,
      action: PayloadAction<{ conversationId: string; messageId: string; content: string; editedAt: number }>
    ) {
      const { conversationId, messageId, content, editedAt } = action.payload;
      const thread = state.threads[conversationId];
      let previewText = content;
      if (thread) {
        const msg = thread.messages.find((m) => m.id === messageId);
        if (msg) {
          msg.content = content;
          msg.editedAt = editedAt;
          previewText = content || (msg.attachment ? msg.attachment.name : "");
        }
      }
      const convo = state.conversations.find((c) => c.id === conversationId);
      if (convo?.lastMessage?.id === messageId) {
        convo.lastMessage = { ...convo.lastMessage, preview: previewText };
      }
      const miniConvo = state.miniChatConversations[conversationId];
      if (miniConvo?.lastMessage?.id === messageId) {
        miniConvo.lastMessage = { ...miniConvo.lastMessage, preview: previewText };
      }
    },
    applyMessageDeleted(
      state,
      action: PayloadAction<{ conversationId: string; messageId: string; deletedAt?: number }>
    ) {
      const { conversationId, messageId, deletedAt } = action.payload;
      const thread = state.threads[conversationId];
      if (thread) {
        const msg = thread.messages.find((m) => m.id === messageId);
        if (msg) {
          msg.deletedAt = deletedAt || Date.now();
          msg.content = "";
          msg.attachment = null;
          msg.reactions = [];
        }
      }
    },
    applyReactionUpdate(
      state,
      action: PayloadAction<
        | {
            conversationId: string;
            messageId: string;
            reactions: MessageReaction[];
            fromSocket?: false;
          }
        | {
            conversationId: string;
            messageId: string;
            reactions: BroadcastMessageReaction[];
            fromSocket: true;
          }
      >
    ) {
      const { conversationId, messageId, reactions, fromSocket } = action.payload;
      const thread = state.threads[conversationId];
      if (!thread) return;
      const msg = thread.messages.find((m) => m.id === messageId);
      if (msg) {
        if (fromSocket) {
          msg.reactions = reactions.map((r) => {
            const local = msg.reactions?.find((lx) => lx.emoji === r.emoji);
            const me =
              r.me !== undefined ? r.me : (local?.me ?? false);
            return { ...r, me };
          });
        } else {
          msg.reactions = reactions;
        }
      }
    },
    updateConversationRead(
      state,
      action: PayloadAction<{
        conversationId: string;
        userId: string;
        lastReadAt: number;
        currentUserId: string;
      }>
    ) {
      const { conversationId, userId, lastReadAt, currentUserId } = action.payload;
      const convo = state.conversations.find((c) => c.id === conversationId);
      const miniConvo = state.miniChatConversations[conversationId];
      if (!convo && !miniConvo) return;

      if (String(userId) === String(currentUserId)) {
        let countToDeduct = 0;
        let companyIdToDeduct: string | null = null;

        if (convo) {
          countToDeduct = convo.unreadCount || 0;
          convo.lastReadAt = lastReadAt;
          convo.unreadCount = 0;
        }
        if (miniConvo) {
          if (!countToDeduct) countToDeduct = miniConvo.unreadCount || 0;
          miniConvo.lastReadAt = lastReadAt;
          miniConvo.unreadCount = 0;
          const miniChat = state.activeMiniChats.find((c) => String(c.conversationId) === String(conversationId));
          if (miniChat) companyIdToDeduct = miniChat.companyId;
          else if (state.conversationCompanyMap[conversationId]) companyIdToDeduct = state.conversationCompanyMap[conversationId];
        }

        if (countToDeduct > 0 && companyIdToDeduct && state.unreadByCompany[companyIdToDeduct]) {
          state.unreadByCompany[companyIdToDeduct].count = Math.max(0, state.unreadByCompany[companyIdToDeduct].count - countToDeduct);
        }
      } else {
        if (convo) {
          if (convo.type === "dm") {
            if (convo.otherParticipant && String(convo.otherParticipant.id) === String(userId)) {
              if (!convo.otherParticipant.lastReadAt || lastReadAt > convo.otherParticipant.lastReadAt) {
                convo.otherParticipant.lastReadAt = lastReadAt;
              }
              if (!convo.maxOtherLastReadAt || lastReadAt > convo.maxOtherLastReadAt) {
                convo.maxOtherLastReadAt = lastReadAt;
              }
            }
          } else if (!convo.maxOtherLastReadAt || lastReadAt > convo.maxOtherLastReadAt) {
            convo.maxOtherLastReadAt = lastReadAt;
          }
        }
        if (miniConvo) {
          if (miniConvo.type === "dm") {
            if (miniConvo.otherParticipant && String(miniConvo.otherParticipant.id) === String(userId)) {
              if (!miniConvo.otherParticipant.lastReadAt || lastReadAt > miniConvo.otherParticipant.lastReadAt) {
                miniConvo.otherParticipant.lastReadAt = lastReadAt;
              }
              if (!miniConvo.maxOtherLastReadAt || lastReadAt > miniConvo.maxOtherLastReadAt) {
                miniConvo.maxOtherLastReadAt = lastReadAt;
              }
            }
          } else if (!miniConvo.maxOtherLastReadAt || lastReadAt > miniConvo.maxOtherLastReadAt) {
            miniConvo.maxOtherLastReadAt = lastReadAt;
          }
        }
      }
    },
    bumpCrossCompanyUnread(
      state,
      action: PayloadAction<{ companyId: string; companyName: string; conversationId?: string }>
    ) {
      const { companyId, companyName, conversationId } = action.payload;
      if (!state.unreadByCompany[companyId]) {
        state.unreadByCompany[companyId] = { count: 0, name: companyName };
      }
      state.unreadByCompany[companyId].count++;
      if (conversationId) {
        state.conversationCompanyMap[conversationId] = companyId;
      }
    },
    clearCrossCompanyUnread(state, action: PayloadAction<string>) {
      const id = action.payload;
      if (state.unreadByCompany[id]) {
        state.unreadByCompany[id] = { ...state.unreadByCompany[id], count: 0 };
      }
    },
    openMiniChat(
      state,
      action: PayloadAction<{ 
        conversationId: string; 
        companyId: string; 
        title?: string; 
        avatarFileId?: string | null;
        conversationType?: Conversation["type"];
        forceExpand?: boolean;
      }>
    ) {
      const { conversationId, companyId, title, avatarFileId, conversationType, forceExpand } = action.payload;
      state.conversationCompanyMap[conversationId] = companyId;
      const existingIdx = state.activeMiniChats.findIndex((c) => String(c.conversationId) === String(conversationId));
      
      if (existingIdx !== -1) {
        state.activeMiniChats[existingIdx].lastActiveAt = Date.now();
        if (forceExpand) {
          state.activeMiniChats[existingIdx].isMinimized = false;
          collapseAllMiniExcept(state, conversationId);
        }
        // Refresh metadata if provided (e.g. placeholder replaced by real name)
        if (title) state.activeMiniChats[existingIdx].title = title;
        if (avatarFileId !== undefined) state.activeMiniChats[existingIdx].avatarFileId = avatarFileId;
        if (conversationType) state.activeMiniChats[existingIdx].conversationType = conversationType;
        return;
      }


      state.activeMiniChats.push({
        conversationId,
        companyId,
        title,
        avatarFileId,
        conversationType,
        isMinimized: false,
        lastActiveAt: Date.now(),
      });
      collapseAllMiniExcept(state, conversationId);
      // Auto-expand launcher when a new chat arrives
      state.isLauncherExpanded = true;
    },
    closeMiniChat(state, action: PayloadAction<string>) {
      state.activeMiniChats = state.activeMiniChats.filter(
        (c) => String(c.conversationId) !== String(action.payload)
      );
    },
    toggleMinimizeMiniChat(state, action: PayloadAction<string>) {
      const id = action.payload;
      const chat = state.activeMiniChats.find(
        (c) => String(c.conversationId) === String(id)
      );
      if (!chat) return;
      const wasMinimized = chat.isMinimized;
      chat.isMinimized = !chat.isMinimized;
      if (wasMinimized && !chat.isMinimized) {
        collapseAllMiniExcept(state, id);
      }
    },
    setMiniChatActive(state, action: PayloadAction<string>) {
      const id = action.payload;
      const chat = state.activeMiniChats.find(
        (c) => String(c.conversationId) === String(id)
      );
      if (!chat) return;
      chat.lastActiveAt = Date.now();
      collapseAllMiniExcept(state, id);
    },
    upsertConversation(state, action: PayloadAction<Conversation>) {
      const convo = action.payload;
      const idx = state.conversations.findIndex(c => c.id === convo.id);
      if (idx >= 0) {
        state.conversations[idx] = { ...state.conversations[idx], ...convo };
      } else {
        if (convo.type === "company") {
          state.conversations.unshift(convo);
        } else {
          state.conversations.push(convo);
        }
      }
    },
    setLauncherExpanded(state, action: PayloadAction<boolean>) {
      state.isLauncherExpanded = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchConversationsThunk.fulfilled, (state, action) => {
      state.conversations = action.payload;
      state.conversationsLoaded = true;
    });
    builder
      .addCase(fetchMessagesThunk.pending, (state, action) => {
        const { conversationId, cursor } = action.meta.arg;
        if (cursor === undefined) return;
        const thread = state.threads[conversationId];
        if (thread) thread.isLoadingOlder = true;
      })
      .addCase(fetchMessagesThunk.fulfilled, (state, action) => {
        const { conversationId, messages, hasMore, nextCursor, cursor } = action.payload;
        const page = normalizeMessagesPage(messages);
        const isOlderPage = cursor !== undefined;

        if (isOlderPage) {
          const thread = state.threads[conversationId];
          if (!thread) return;
          thread.messages = mergeMessagePages(page, thread.messages);
          thread.hasMore = hasMore;
          thread.nextCursor = nextCursor;
          thread.isLoadingOlder = false;
          return;
        }

        const existing = state.threads[conversationId];
        state.threads[conversationId] = {
          conversationId,
          messages: existing?.messages?.length
            ? mergeMessagePages(page, existing.messages)
            : page,
          hasMore,
          nextCursor,
          isLoadingOlder: false,
          historyLoaded: true,
        };
      })
      .addCase(fetchMessagesThunk.rejected, (state, action) => {
        const { conversationId, cursor } = action.meta.arg;
        let thread = state.threads[conversationId];
        if (cursor === undefined) {
          if (!thread) {
            thread = {
              conversationId,
              messages: [],
              hasMore: true,
              nextCursor: null,
              isLoadingOlder: false,
              historyLoaded: false,
            };
            state.threads[conversationId] = thread;
          }
        } else {
          if (thread) thread.isLoadingOlder = false;
        }
      })
      .addCase(fetchMiniChatConversationMetaThunk.fulfilled, (state, action) => {
        const { conversationId, companyId, convo } = action.payload;
        state.conversationCompanyMap[conversationId] = companyId;

        const activelyViewing = isMiniChatActivelyViewed(state, conversationId);
        const convoToStore = activelyViewing
          ? { ...convo, unreadCount: 0 }
          : convo;

        // Reconcile cross-company badge only when the user is not reading this thread.
        if (!activelyViewing) {
          const oldConvo = state.miniChatConversations[conversationId];
          const oldCount = oldConvo?.unreadCount || 0;
          const newCount = convoToStore.unreadCount || 0;
          const diff = newCount - oldCount;
          if (diff !== 0 && state.unreadByCompany[companyId]) {
            state.unreadByCompany[companyId].count = Math.max(
              0,
              state.unreadByCompany[companyId].count + diff
            );
          }
        }

        state.miniChatConversations[conversationId] = convoToStore;
        const chat = state.activeMiniChats.find(
          (c) => String(c.conversationId) === String(conversationId)
        );
        if (!chat) return;
        chat.conversationType = convo.type;
        if (convo.type === "dm" && convo.otherParticipant) {
          chat.title = convo.otherParticipant.name;
          chat.avatarFileId = convo.otherParticipant.avatarFileId;
        }
      });
    builder.addCase(fetchUnreadByCompanyThunk.fulfilled, (state, action) => {
      state.unreadByCompany = action.payload;
    });
  },
});

export const {
  setActiveTab,
  openConversation,
  receiveMessage,
  addOptimisticMessage,
  replaceOptimisticMessage,
  removeOptimisticMessage,
  bumpUnread,
  clearUnread,
  applyMessageEdited,
  applyMessageDeleted,
  applyReactionUpdate,
  updateConversationRead,
  bumpCrossCompanyUnread,
  clearCrossCompanyUnread,
  openMiniChat,
  closeMiniChat,
  toggleMinimizeMiniChat,
  setMiniChatActive,
  upsertConversation,
  setLauncherExpanded,
} = chatSlice.actions;

export default chatSlice.reducer;
